'use client';

import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

/** How far the user has to scroll before the cue has served its purpose. */
const HIDE_AFTER_PX = 120;

/**
 * Bottom-of-viewport hint that there's more page below the fold. It only shows
 * while the user is still at the top, and only when the page actually scrolls,
 * so it never lingers or lies about content that isn't there.
 */
export function ScrollCue() {
  const [visible, setVisible] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const update = () => {
      const hasMoreBelow =
        document.documentElement.scrollHeight - window.innerHeight >
        HIDE_AFTER_PX;
      setVisible(hasMoreBelow && window.scrollY < HIDE_AFTER_PX);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const scrollDown = () =>
    window.scrollTo({
      top: window.innerHeight * 0.85,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });

  return (
    <AnimatePresence>
      {visible && (
        /* A fade band anchored to the bottom of the viewport. It stops the cards
           from running straight into the fold, which is what made the bare
           chevron impossible to pick out, and the softened edge is itself a hint
           that the page continues. */
        <motion.div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex h-28 items-end justify-center bg-gradient-to-t from-[#cdc4c1] via-[#cdc4c1]/70 to-transparent pb-5 md:h-32 md:pb-7"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* The wrapper owns the centering so framer-motion's inline transform
              on the button doesn't fight a -translate-x-1/2 class. */}
          <motion.button
            type="button"
            onClick={scrollDown}
            aria-label="Scroll down for more"
            className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#786D67] text-white shadow-md ring-1 ring-black/5 transition-colors hover:bg-[#5F5751] md:h-14 md:w-14"
            animate={reduceMotion ? undefined : { y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ChevronDown
              className="h-6 w-6 md:h-7 md:w-7"
              strokeWidth={2.25}
              aria-hidden="true"
            />
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
